import { useState } from 'react';
import { Check } from 'lucide-react';

import { Button } from '@/components/Button';
import { Field } from '@/components/Field';
import { toast } from '@/lib/toast';
import { useAiSettings, useSaveAiSettings } from './useBraindump';

/**
 * Anthropic key management.
 *
 * Bring-your-own key: it is encrypted with the same envelope as the calendar
 * credentials and never returned to the browser again, only its last four
 * characters, so you can tell which key is saved without the app being able to
 * hand it back.
 */
export function AiSettingsPanel() {
  const settings = useAiSettings();
  const save = useSaveAiSettings();

  const [key, setKey] = useState('');
  const [budget, setBudget] = useState('');

  if (!settings.data) {
    return <div className="h-32 animate-pulse rounded-lg bg-surface-2" aria-hidden />;
  }

  const data = settings.data;

  return (
    <section className="flex flex-col gap-4 rounded-lg border border-border bg-surface-1 p-4">
      {/* Key state first: whether a key exists is the one thing you came to
          check, and it decides whether the rest of the form is a replace or
          a first save. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {data.configured ? (
          <p className="flex items-center gap-2 text-small text-success">
            <Check size={14} strokeWidth={2} aria-hidden />
            Key saved, ending {data.api_key_hint}
          </p>
        ) : (
          <p className="text-small text-text-tertiary">No key saved.</p>
        )}

        <label className="inline-flex min-h-touch items-center gap-2">
          <input
            type="checkbox"
            checked={data.enabled}
            onChange={(e) => save.mutate({ enabled: e.target.checked })}
            className="size-icon accent-[var(--accent)]"
          />
          <span className="text-small text-text-secondary">Enable AI parsing</span>
        </label>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <Field
        label={data.configured ? 'Replace key' : 'Anthropic API key'}
        type="password"
        autoComplete="off"
        value={key}
        onChange={(e) => setKey(e.target.value)}
        placeholder="sk-ant-…"
        hint="Encrypted before it is stored. Never shown again."
      />

      <Field
        label="Monthly spend cap"
        inputMode="decimal"
        value={budget}
        onChange={(e) => setBudget(e.target.value)}
        placeholder={data.monthly_budget_usd ?? 'no cap'}
        hint={`$${Number(data.month_to_date_usd).toFixed(2)} used this month.`}
        className="tabular"
      />
      </div>

      <div>
        <label
          htmlFor="ai-model"
          className="mb-2 block text-small font-medium text-text-secondary"
        >
          Model
        </label>
        <select
          id="ai-model"
          value={data.model}
          onChange={(e) => save.mutate({ model: e.target.value })}
          className="min-h-touch w-full rounded border border-border bg-surface-1 px-2 text-body outline-none focus-visible:ring-2 focus-visible:ring-ring focus:border-accent-border"
        >
          {data.available_models.map((option) => (
            <option key={option.id} value={option.id}>
              {option.id}: ${option.input_per_mtok}/${option.output_per_mtok} per Mtok
            </option>
          ))}
        </select>
        <p className="mt-1 text-small text-text-tertiary">
          Braindump is extraction, not hard reasoning. Haiku handles it at a fifth of
          Opus pricing and returns much faster.
        </p>
      </div>

      {data.last_error && (
        <p role="alert" className="text-small text-danger">
          Last error: {data.last_error}
        </p>
      )}

      <div className="flex justify-end gap-2">
        {data.configured && (
          <Button
            variant="ghost"
            className="text-danger"
            onClick={() =>
              save.mutate(
                { api_key: '' } as never,
                { onSuccess: () => toast.show('Key removed') },
              )
            }
          >
            Remove key
          </Button>
        )}
        <Button
          variant="primary"
          loading={save.isPending}
          disabled={!key.trim() && !budget.trim()}
          onClick={() =>
            save.mutate(
              {
                ...(key.trim() ? { api_key: key.trim() } : {}),
                ...(budget.trim() ? { monthly_budget_usd: budget.trim() } : {}),
              } as never,
              {
                onSuccess: () => {
                  setKey('');
                  setBudget('');
                  toast.show('Saved');
                },
              },
            )
          }
        >
          Save
        </Button>
      </div>
    </section>
  );
}
